import { useCallback, useEffect, useMemo, useState } from "react";
import { tr } from "../../i18n/tr";
import { ClipboardList, CheckCircle2, Clock, Loader2, Save, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

/* Iter 322 — Correction des évaluations par le formateur (fonctionne hors ligne : notes mises en file d'attente). */
export function TrainerEvaluations({ api, sessionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null); // { evaluation, submission }
  const url = `/formateur/sessions/${sessionId}/evaluations`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(url);
      setData(r.data);
    } catch (e) { if (!e.offline) toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  // eslint-disable-next-line
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const onGraded = (submissionId, grading, queued) => {
    const patch = (d) => ({
      ...d,
      evaluations: (d?.evaluations || []).map((ev) => ({
        ...ev,
        submissions: (ev.submissions || []).map((s) => (s.submission_id === submissionId ? { ...s, grading: { ...grading, pending: queued } } : s)),
        stats: { ...ev.stats, graded: (ev.submissions || []).filter((s) => s.submission_id === submissionId ? true : !!s.grading?.graded_at).length },
      })),
    });
    setData(patch);
    api.patchCache?.(url, patch);
    setSel(null);
  };

  if (loading && !data) return <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--brand-primary,#f7c935)]" /></div>;
  const evals = data?.evaluations || [];

  if (sel) {
    return <GradingPanel api={api} sessionId={sessionId} evaluation={sel.evaluation} submission={sel.submission} onBack={() => setSel(null)} onGraded={onGraded} />;
  }

  return (
    <div className="space-y-5" data-testid="trainer-evaluations">
      {evals.length === 0 && (
        <div className="py-14 text-center border border-dashed border-white/10 rounded-2xl text-sm text-slate-500" data-testid="trainer-evaluations-empty">
          Aucune évaluation rattachée à cette session (Programme → Rattachements → Évaluations).
        </div>
      )}
      {evals.map((ev) => (
        <div key={ev.evaluation_id} className="bg-[#14101c] border border-white/10 rounded-2xl p-5" data-testid={"trainer-eval-" + ev.evaluation_id}>
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--brand-primary,#f7c935)] font-bold mb-1 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Évaluation · {ev.eval_type || ""}</div>
              <h3 className="text-lg font-black text-white">{ev.title}</h3>
              {ev.description && <p className="text-xs text-slate-400 mt-1">{ev.description}</p>}
            </div>
            <div className="text-right text-[11px] text-slate-400" data-testid={"trainer-eval-stats-" + ev.evaluation_id}>
              <div><span className="text-white font-bold">{ev.stats?.submitted || 0}</span>/{ev.stats?.enrolled || 0} réponses</div>
              <div><span className="text-emerald-400 font-bold">{ev.stats?.graded || 0}</span> {tr("lms.corrigees_b4b6", "corrigées")}</div>
            </div>
          </div>
          {(ev.submissions || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">{tr("lms.aucune_reponse_de_stagiaire_59ca", "Aucune réponse de stagiaire pour l'instant.")}</div>
          ) : (
            <div className="divide-y divide-white/5 border border-white/5 rounded-xl overflow-hidden">
              {ev.submissions.map((s) => {
                const g = s.grading;
                return (
                  <button key={s.submission_id} onClick={() => setSel({ evaluation: ev, submission: s })} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/5" data-testid={"trainer-sub-" + s.submission_id}>
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-xs font-bold text-white">{(s.student_name || "?").slice(0, 1).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{s.student_name}</div>
                      <div className="text-[10px] text-slate-500">Répondu le {new Date(s.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}{s.avg_rating != null && ` · satisfaction ${Number(s.avg_rating).toFixed(1)}/5`}</div>
                    </div>
                    {g?.graded_at ? (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center gap-1" data-testid={"trainer-sub-grade-" + s.submission_id}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> {g.score != null ? `${g.score}/${g.max_score}` : "Corrigé"}{g.pending && " · en attente de synchro"}
                      </span>
                    ) : (
                      <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {tr("lms.a_corriger_8324", "À corriger")}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function answerText(q, v) {
  if (v == null || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (q.type === "yesno") return v === true || v === "yes" || v === "oui" ? "Oui" : "Non";
  if (q.type === "rating") return `${v} / ${q.max_value || 5}`;
  if (q.type === "nps") return `${v} / 10`;
  return String(v);
}

function GradingPanel({ api, sessionId, evaluation, submission, onBack, onGraded }) {
  const questions = evaluation.questions || [];
  const g0 = submission.grading || {};
  const [perQ, setPerQ] = useState(() => {
    const init = {};
    questions.forEach((q) => { const p = g0.per_question?.[q.question_id] || {}; init[q.question_id] = { points: p.points ?? "", comment: p.comment || "" }; });
    return init;
  });
  const [maxScore, setMaxScore] = useState(g0.max_score ?? 20);
  const [comment, setComment] = useState(g0.comment || "");
  const [saving, setSaving] = useState(false);
  const autoScore = useMemo(() => Object.values(perQ).reduce((acc, p) => acc + (p.points === "" ? 0 : Number(p.points) || 0), 0), [perQ]);
  const [score, setScore] = useState(g0.score ?? null);
  const finalScore = score ?? autoScore;

  const save = async () => {
    if (finalScore < 0 || finalScore > Number(maxScore)) { toast.error(tr("lms.score_hors_bornes_bf31", "Score hors bornes")); return; }
    setSaving(true);
    try {
      const body = { per_question: perQ, max_score: Number(maxScore), score: finalScore, comment, client_id: `grade_${submission.submission_id}_${Date.now()}` };
      const r = await api.put(`/formateur/sessions/${sessionId}/submissions/${submission.submission_id}/grade`, body);
      const grading = r.queued ? { ...body, graded_at: new Date().toISOString() } : r.data.grading;
      toast.success(r.queued ? "Note enregistrée (hors ligne) — synchronisation au retour du réseau." : tr("lms.note_enregistree_483a", "Note enregistrée."));
      onGraded(submission.submission_id, grading, !!r.queued);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5" data-testid="grading-panel">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={onBack} className="text-slate-400 hover:text-white text-xs flex items-center gap-1" data-testid="grading-back"><ChevronLeft className="w-4 h-4" /> {tr("lms.retour_7727", "Retour")}</button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{evaluation.title}</div>
          <h3 className="text-lg font-black text-white truncate">{submission.student_name}</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{tr("lms.note_3b06", "Note")}</div>
          <div className="flex items-center gap-1 justify-end">
            <input type="number" value={finalScore} onChange={(e) => setScore(e.target.value === "" ? null : Number(e.target.value))} className="w-16 bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-right text-white font-black" data-testid="grading-score" />
            <span className="text-slate-500">/</span>
            <input type="number" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} className="w-14 bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-right text-slate-300" data-testid="grading-max" />
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {questions.map((q, i) => {
          const v = submission.answers?.[q.question_id];
          const p = perQ[q.question_id] || { points: "", comment: "" };
          return (
            <div key={q.question_id} className="border border-white/5 rounded-xl p-4 bg-[#0b0811]/40" data-testid={"grading-q-" + q.question_id}>
              <div className="text-xs font-semibold text-slate-300 mb-1">{i + 1}. {q.text}</div>
              <div className="text-sm text-white bg-white/5 rounded-lg px-3 py-2 mb-3 whitespace-pre-wrap" data-testid={"grading-answer-" + q.question_id}>{answerText(q, v)}</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="number" placeholder={tr("lms.points_75dd", "Points")} value={p.points} onChange={(e) => setPerQ((s) => ({ ...s, [q.question_id]: { ...p, points: e.target.value } }))} className="w-full sm:w-24 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid={"grading-points-" + q.question_id} />
                <input placeholder={tr("lms.commentaire_pour_le_stagiaire_68ff", "Commentaire pour le stagiaire")} value={p.comment} onChange={(e) => setPerQ((s) => ({ ...s, [q.question_id]: { ...p, comment: e.target.value } }))} className="flex-1 bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid={"grading-comment-" + q.question_id} />
              </div>
            </div>
          );
        })}
        {submission.comment && <div className="text-xs text-slate-400 italic">Commentaire du stagiaire : « {submission.comment} »</div>}
        <textarea rows={3} placeholder={tr("lms.appreciation_generale_12e3", "Appréciation générale")} value={comment} onChange={(e) => setComment(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="grading-global-comment" />
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500">{tr("lms.total_des_points_c8fa", "Total des points :")} <span className="text-white font-bold">{autoScore}</span> · {score != null && score !== autoScore ? "note ajustée manuellement" : "note = somme des points"}</div>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-full text-xs font-bold bg-[var(--brand-primary,#f7c935)] text-[#14101c] flex items-center gap-1.5 disabled:opacity-50" data-testid="grading-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer la note
          </button>
        </div>
      </div>
    </div>
  );
}

export default TrainerEvaluations;
